import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, toast } from "../App";

export default function Login({ setUser }) {
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post("/auth/login", loginData);
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      setUser(response.data.user);
      toast.success("Login successful!");
      
      // RBAC-based: Redirect using role's default page
      const user = response.data.user;
      
      // Check if user has any page permissions
      const hasAnyAccess = user.permissions && Object.values(user.permissions).some(perm => perm?.view === true);
      
      if (!hasAnyAccess) {
        // No page access - redirect to no-access page
        navigate("/no-access");
        return;
      }
      
      // Use default_page from role configuration (preferred method)
      if (user.default_page) {
        navigate(user.default_page);
      } else {
        // Fallback: try common pages in order
        if (user.permissions?.dashboardEstimation?.view === true) {
          navigate("/dashboard-estimation");
        } else if (user.permissions?.sales?.view === true) {
          navigate("/sales");
        } else if (user.permissions?.dashboard?.view === true) {
          navigate("/dashboard");
        } else if (user.permissions?.projects?.view === true) {
          navigate("/projects");
        } else {
          // Last resort: root
          navigate("/");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" 
      style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
    >
      {/* MD3 Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-md3-primary/20 to-md3-tertiary/20" />
      
      <div className="w-full max-w-md relative z-10">
        {/* Logo and Title Section */}
        <div className="text-center mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="flex items-center justify-center mb-6">
            <img 
              src="/logo.png" 
              alt="Milekal Logo" 
              className="h-24 w-auto drop-shadow-2xl"
            />
          </div>
          <h1 className="text-md3-display-small text-white mb-2 drop-shadow-lg font-light">
            Milekal Pulse
          </h1>
          <p className="text-md3-body-large text-white/90 drop-shadow-md">
            Track, Manage, Deliver
          </p>
        </div>

        {/* MD3 Card - Elevated with prominent shadow */}
        <div 
          className="bg-md3-surface-container backdrop-blur-md rounded-md3-xl shadow-md3-3 
                     hover:shadow-md3-4 transition-all duration-300 p-8
                     animate-in fade-in slide-in-from-bottom-8 duration-700"
          data-testid="login-card"
        >
          {/* Card Header */}
          <div className="mb-8">
            <h2 className="text-md3-headline-small text-md3-on-surface font-semibold mb-2">
              Welcome Back
            </h2>
            <p className="text-md3-body-medium text-gray-600">
              Enter your credentials to access your account
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            {/* Username Input - MD3 Filled Style */}
            <div className="relative">
              <label 
                htmlFor="login-username"
                className="text-md3-label-medium text-gray-700 font-medium mb-1 block"
              >
                Username
              </label>
              <div className="relative">
                <input
                  id="login-username"
                  data-testid="login-username-input"
                  type="text"
                  placeholder="Enter username"
                  value={loginData.username}
                  onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                  onFocus={() => setFocusedField('username')}
                  onBlur={() => setFocusedField(null)}
                  required
                  className="w-full px-4 py-3.5 
                           bg-md3-surface-container-high
                           text-md3-on-surface text-md3-body-large
                           rounded-md3-sm rounded-b-none
                           border-b-2 transition-all duration-200
                           focus:outline-none
                           hover:bg-md3-surface-container-highest
                           placeholder:text-md3-on-surface-variant/60"
                  style={{
                    borderBottomColor: focusedField === 'username' ? '#6750A4' : '#79747E'
                  }}
                />
                {/* MD3 Active Indicator */}
                <div 
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-md3-primary 
                             transform transition-transform duration-200 origin-center"
                  style={{
                    transform: focusedField === 'username' ? 'scaleX(1)' : 'scaleX(0)'
                  }}
                />
              </div>
            </div>

            {/* Password Input - MD3 Filled Style */}
            <div className="relative">
              <label 
                htmlFor="login-password"
                className="text-md3-label-medium text-gray-700 font-medium mb-1 block"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="login-password"
                  data-testid="login-password-input"
                  type="password"
                  placeholder="Enter password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  required
                  className="w-full px-4 py-3.5 
                           bg-md3-surface-container-high
                           text-md3-on-surface text-md3-body-large
                           rounded-md3-sm rounded-b-none
                           border-b-2 transition-all duration-200
                           focus:outline-none
                           hover:bg-md3-surface-container-highest
                           placeholder:text-md3-on-surface-variant/60"
                  style={{
                    borderBottomColor: focusedField === 'password' ? '#6750A4' : '#79747E'
                  }}
                />
                {/* MD3 Active Indicator */}
                <div 
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-md3-primary 
                             transform transition-transform duration-200 origin-center"
                  style={{
                    transform: focusedField === 'password' ? 'scaleX(1)' : 'scaleX(0)'
                  }}
                />
              </div>
            </div>

            {/* Login Button - MD3 Filled Style */}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full relative overflow-hidden
                       bg-md3-primary hover:bg-md3-primary/90
                       text-md3-on-primary text-md3-label-large
                       rounded-md3-full py-3.5 px-6
                       shadow-md3-1 hover:shadow-md3-2
                       active:shadow-md3-1 active:scale-[0.98]
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all duration-200
                       font-medium
                       group"
            >
              {/* MD3 State Layer (Ripple Effect) */}
              <span className="absolute inset-0 bg-white/0 group-hover:bg-white/10 
                             group-active:bg-white/20 transition-colors duration-200" />
              
              <span className="relative z-10">
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Logging in...
                  </span>
                ) : (
                  'Login'
                )}
              </span>
            </button>
          </form>

          {/* Footer Text */}
          <div className="mt-8 text-center">
            <p className="text-md3-body-small text-md3-on-surface-variant">
              Need an account? Contact your administrator.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
